import { useCallback, useEffect, useMemo, useState } from 'react';
import capitalize from 'lodash/capitalize';
import { useSelector } from 'react-redux';
import { getCartPersonalDetails } from '@yme/common/redux/cart/selectors';
import { getProfileData } from '@yme/components/Header/ProfileMenu/redux/selectors';
import { DISCOUNT_KEY, EMAIL_KEY, FIRST_NAME_KEY, LAST_NAME_KEY, PERSONAL_DETAILS_EMAIL_KEY, PERSONAL_DETAILS_FIRST_NAME_KEY, PERSONAL_DETAILS_LAST_NAME_KEY, PERSONAL_DETAILS_PHONE_KEY, PHONE_KEY, SESSION_STORAGE_GUEST_KEY, } from './constants';
import { getValidation } from './helpers';
export function useValidation({ configurationArray }) {
    const [configure, setConfigure] = useState([]);
    const [emailValue, setEmailValue] = useState('');
    const [fistNameValue, setFistNameValue] = useState('');
    const [lastNameValue, setLastNameValue] = useState('');
    const [phoneValue, setPhoneValue] = useState('');
    const [personalDetailsEmailValue, setPersonalDetailsEmailValue] = useState('');
    const [personalDetailsFistNameValue, setPersonalDetailsFistNameValue] = useState('');
    const [personalDetailsLastNameValue, setPersonalDetailsLastNameValue] = useState('');
    const [personalDetailsPhoneValue, setPersonalDetailsPhoneValue] = useState('');
    const [discountValue, setDiscountValue] = useState('');
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
    const { firstName, lastName, phoneNumber, email } = useSelector(getProfileData);
    const { cartFirstName, cartLastName, cartPhone, cartEmail } = useSelector(getCartPersonalDetails);
    const asGuestEmail = sessionStorage.getItem(SESSION_STORAGE_GUEST_KEY) || '';
    const setIsEmptyProperty = (elementType, value) => {
        if (!value.trim().length) {
            setConfigure((prev) => prev.map((item) => item.type === elementType
                ? {
                    ...item,
                    isEmpty: true,
                }
                : item));
        }
        else {
            setConfigure((prev) => prev.map((item) => item.type === elementType
                ? {
                    ...item,
                    isEmpty: false,
                }
                : item));
        }
    };
    const setErrorsInField = (elementType, value, isRequired = false) => {
        if (!getValidation(value, elementType, isRequired)) {
            const error = ['error'];
            setConfigure((prev) => prev.map((item) => item.type === elementType
                ? {
                    ...item,
                    errorCode: error,
                    isValid: false,
                }
                : item));
        }
        if (getValidation(value, elementType, isRequired)) {
            const error = [];
            setConfigure((prev) => prev.map((item) => item.type === elementType
                ? {
                    ...item,
                    errorCode: error,
                    isValid: true,
                }
                : item));
        }
    };
    const onFirstNameChangeHandler = (value) => {
        setIsEmptyProperty(FIRST_NAME_KEY, value);
        setIsEmptyProperty(PERSONAL_DETAILS_FIRST_NAME_KEY, value);
        setFistNameValue(capitalize(value));
        setPersonalDetailsFistNameValue(capitalize(value));
    };
    const onLastNameChangeHandler = (value) => {
        setIsEmptyProperty(LAST_NAME_KEY, value);
        setIsEmptyProperty(PERSONAL_DETAILS_LAST_NAME_KEY, value);
        setLastNameValue(capitalize(value));
        setPersonalDetailsLastNameValue(capitalize(value));
    };
    const onPhoneChangeHandler = (value) => {
        setIsEmptyProperty(PHONE_KEY, value);
        setIsEmptyProperty(PERSONAL_DETAILS_PHONE_KEY, value);
        setPhoneValue(value.trim());
        setPersonalDetailsPhoneValue(value.trim());
    };
    const onEmailChangeHandler = (value) => {
        setIsEmptyProperty(EMAIL_KEY, value);
        setIsEmptyProperty(PERSONAL_DETAILS_EMAIL_KEY, value);
        setEmailValue(value.trim());
        setPersonalDetailsEmailValue(value.trim());
    };
    const onDiscountChangeHandler = (value) => {
        setIsEmptyProperty(DISCOUNT_KEY, value);
        setDiscountValue(value);
    };
    const onFocus = (elementType) => {
        setConfigure((prev) => prev.map((item) => item.type === elementType
            ? {
                ...item,
                isTouched: true,
                errorCode: [],
            }
            : item));
    };
    const onBlur = (value, elementType, isRequired) => {
        setErrorsInField(elementType, value, isRequired);
    };
    const setPreselectedProperty = useCallback((elementType, preselectedFieldValue) => {
        if (preselectedFieldValue?.length > 0) {
            setIsEmptyProperty(elementType, preselectedFieldValue);
            setErrorsInField(elementType, preselectedFieldValue);
        }
    }, []);
    useEffect(() => {
        setConfigure(configurationArray);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const personalDetailFirstName = cartFirstName || firstName;
        setFistNameValue(firstName);
        setPersonalDetailsFistNameValue(personalDetailFirstName);
        setPreselectedProperty(FIRST_NAME_KEY, firstName);
        setPreselectedProperty(PERSONAL_DETAILS_FIRST_NAME_KEY, personalDetailFirstName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstName, cartFirstName]);
    useEffect(() => {
        const personalDetailLastName = cartLastName || lastName;
        setLastNameValue(lastName);
        setPersonalDetailsLastNameValue(personalDetailLastName);
        setPreselectedProperty(LAST_NAME_KEY, lastName);
        setPreselectedProperty(PERSONAL_DETAILS_LAST_NAME_KEY, personalDetailLastName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastName, cartLastName]);
    useEffect(() => {
        const personalDetailPhone = cartPhone || phoneNumber;
        setPhoneValue(phoneNumber);
        setPersonalDetailsPhoneValue(personalDetailPhone);
        setPreselectedProperty(PHONE_KEY, phoneNumber);
        setPreselectedProperty(PERSONAL_DETAILS_PHONE_KEY, personalDetailPhone);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneNumber, cartPhone]);
    useEffect(() => {
        const personalDetailsPreselectedEmail = email || asGuestEmail || cartEmail;
        setPersonalDetailsEmailValue(personalDetailsPreselectedEmail);
        setEmailValue(email);
        setPreselectedProperty(EMAIL_KEY, email);
        setPreselectedProperty(PERSONAL_DETAILS_EMAIL_KEY, personalDetailsPreselectedEmail);
    }, [asGuestEmail, cartEmail, email, setPreselectedProperty]);
    useEffect(() => {
        const isValid = configure.every((item) => item.required ? !item.errorCode.length && !item.isEmpty : !item.errorCode.length);
        setIsSubmitButtonDisabled(!isValid);
    }, [configure]);
    const isCurrentEmailPreselected = useMemo(() => {
        return Boolean(email === personalDetailsEmailValue ||
            cartEmail === personalDetailsEmailValue ||
            asGuestEmail === personalDetailsEmailValue);
    }, [email, personalDetailsEmailValue, cartEmail, asGuestEmail]);
    return {
        configure,
        isSubmitButtonDisabled,
        onFocus,
        onBlur,
        setConfigure,
        fistNameValue,
        lastNameValue,
        isCurrentEmailPreselected,
        cartEmail,
        asGuestEmail,
        authorizedEmail: email,
        phoneValue,
        emailValue,
        discountValue,
        personalDetailsEmailValue,
        personalDetailsFistNameValue,
        personalDetailsLastNameValue,
        personalDetailsPhoneValue,
        onFirstNameChangeHandler,
        onLastNameChangeHandler,
        onPhoneChangeHandler,
        onEmailChangeHandler,
        onDiscountChangeHandler,
    };
}
